/**
 * @fileoverview Pure helpers for substitute requests (docs/substitute-requests.md).
 *
 * The server decides what an instructor may request (`requestable` on each
 * session) and what a session is (`covered`); these only turn those answers
 * into words and choices, so the org portal, the instructor pages and the
 * substitute's own page describe one policy the same way.
 */

/** What an org that never set a policy reads as — the server's own default. */
export const DEFAULT_SUBSTITUTE_POLICY = {
  enabled: false,
  min_lead_hours: 48,
  max_requests: 3,
  lesson_plan_required: false,
};

/** Notice presets, in hours. A stored value that is not one of these is still offered. */
export const LEAD_TIME_PRESETS = [0, 12, 24, 48, 72, 168, 336];

/** Request-limit presets; `null` is "no limit". */
export const LIMIT_PRESETS = [1, 2, 3, 4, 5, 6, 8, 10, 15, 20, null];

/** "48 hours" → "2 days"; whole days and weeks read as such, anything else in hours. */
export function leadTimeLabel(hours) {
  const h = Number(hours) || 0;
  if (h === 0) return "No minimum";
  if (h % 168 === 0) return h === 168 ? "1 week" : `${h / 168} weeks`;
  if (h % 24 === 0) return h === 24 ? "24 hours" : `${h / 24} days`;
  return `${h} hour${h === 1 ? "" : "s"}`;
}

/** "2 days' notice", "1 week's notice", "no minimum notice" — the possessive a sentence needs. */
export function noticePhrase(hours) {
  const label = leadTimeLabel(hours);
  if (label === "No minimum") return "no minimum notice";
  const plural = !/^1 /.test(label);
  return `${label}${plural ? "'" : "'s"} notice`;
}

/** "3 requests" / "No limit". */
export function limitLabel(max) {
  if (max === null || max === undefined) return "No limit";
  return `${max} request${max === 1 ? "" : "s"}`;
}

/** The whole policy in one line, for "Use the organization default (…)". */
export function policySummary(policy) {
  const p = { ...DEFAULT_SUBSTITUTE_POLICY, ...(policy || {}) };
  if (!p.enabled) return "Off";
  return [
    noticePhrase(p.min_lead_hours),
    p.max_requests === null || p.max_requests === undefined
      ? "no request limit"
      : `up to ${limitLabel(p.max_requests)} per instructor`,
    p.lesson_plan_required ? "lesson plan required" : "lesson plan optional",
  ].join(" · ");
}

/** Requests left before the instructor has to contact an admin, or null for no limit. */
export function requestsLeft(substitutes) {
  if (!substitutes || substitutes.max_requests === null || substitutes.max_requests === undefined) return null;
  return Math.max(0, substitutes.max_requests - (substitutes.requests_used || 0));
}

/** Status words for a request, as both portals show them. */
export const REQUEST_STATUS = {
  pending: { label: "Sub requested", accent: true },
  approved: { label: "Sub approved" },
  declined: { label: "Declined" },
  withdrawn: { label: "Withdrawn" },
};

/** Who covers a session, in words: "Jamie Chen", or the promise when unnamed. */
export function coverName(cover) {
  if (!cover) return null;
  if (cover.kind === "unnamed" || !cover.name) return "a substitute (to be confirmed)";
  return cover.name;
}

/**
 * The error a request came back with, as `{code, message, ...}`, whatever
 * shape the detail took — so the dialog can switch on `code`.
 */
export function requestError(err) {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === "object" && !Array.isArray(detail)) return detail;
  return { code: null, message: typeof detail === "string" ? detail : null };
}
